import { Set as ImmutableSet } from 'immutable';
import emptyFunction from 'react-utils/emptyFunction';
import invariant from 'react-utils/invariant';

const VALIDATOR = Symbol('validator');
const VALUE = Symbol('value');
const WATCHERS = Symbol('watchers');

type Validator<T> = (testValue: T, currentValue: T) => boolean;
export type Watcher<T> = (
  value: T,
  previousValue: T,
  atomInstance: Atom<T>
) => void;

/**
 * You probably shouldn't use this in your app. Atoms are a lighter weight
 * solution to the same problems GeneralStore or Redux address. If you already
 * use flux you should continue to do so.
 *
 * This is an adaption of Clojure's atom.
 * http://clojure.org/reference/atoms
 *
 * An Atom is an observable wrapper around some value.
 * It's a simple alternative to more complex state management structures like
 * GeneralStore or redux.
 */
class Atom<T> {
  private readonly [VALIDATOR]: Validator<unknown>;
  private [VALUE]: T;
  private [WATCHERS]: ImmutableSet<Watcher<T>>;

  [key: string | symbol]: any;

  constructor(
    value: T = undefined as T,
    validator: Validator<unknown> = emptyFunction.thatReturns(true)
  ) {
    this[VALIDATOR] = validator;
    this[VALUE] = value;
    this[WATCHERS] = ImmutableSet();
  }

  toString(): string {
    return `Atom<${this[VALUE]}>`;
  }
}

export type AtomType<T> = InstanceType<typeof Atom<T>>;

/**
 * Retrieves the value stored inside the atomInstance.
 * "deref" is short for "dereference".
 *
 * @param  Atom<T>
 * @return T
 */
export function deref<T>(atomInstance: Atom<T>): T {
  return atomInstance[VALUE];
}

/**
 * Creates a new Atom.
 * Takes an initial value and an optional validator function.
 *
 * @param  T
 * @param  ?(testValue: T, currentValue: T) => bool
 * @return Atom<T>
 */
function makeAtom<T>(initialValue: T, validator?: Validator<unknown>): Atom<T> {
  invariant(
    !validator || typeof validator === 'function',
    'expected `validator` to be a function or undefined but got `%s`',
    validator
  );
  return new Atom(initialValue, validator);
}

export { makeAtom as atom };

/**
 * Takes a value and returns true if that value is an Atom.
 *
 * @param  any
 * @return bool
 */
export function isAtom(thing: unknown): thing is Atom<unknown> {
  return thing instanceof Atom;
}

/**
 * Takes an Atom and some value and returns true if the value may be set into
 * the Atom according to the Atom's validator.
 *
 * @param  Atom<T>
 * @param  T
 * @return bool
 */
export function isValidValue<T>(atomInstance: Atom<T>, value: T): boolean {
  return atomInstance[VALIDATOR](value, deref(atomInstance));
}

/**
 * Used internally to set the value of an Atom.
 *
 * @param  Atom<T>
 * @param  T
 * @return Atom<T>
 */
function transition<T>(atomInstance: Atom<T>, nextValue: T): Atom<T> {
  invariant(
    isValidValue(atomInstance, nextValue),
    'invalid atomInstance value `%s`',
    nextValue
  );
  const previousValue = atomInstance[VALUE];
  atomInstance[VALUE] = nextValue;
  atomInstance[WATCHERS].forEach((watcher: Watcher<T>) =>
    watcher(nextValue, previousValue, atomInstance)
  );
  return atomInstance;
}

/**
 * Overwrites the value of an Atom.
 * Generally it's better to use swap to avoid race conditions.
 *
 * @param  Atom<T>
 * @param  T
 * @return Atom<T>
 */
export function reset<T>(atomInstance: Atom<T>, nextValue: T): Atom<T> {
  return transition(atomInstance, nextValue);
}

/**
 * Takes an atomInstance and a transition function which  is applied to the current
 * value of the Atom to produce a new one.
 *
 * @param  Atom<T>
 * @param  (currentValue: T) => T
 * @return Atom<T>
 */
export function swap<T>(
  atomInstance: Atom<T>,
  swapper: (currentValue: T) => T
): Atom<T> {
  return transition(atomInstance, swapper(deref(atomInstance)));
}

/**
 * Registers a callback that will be run each time the Atom's value changes.
 * A given callback will only be run once, no matter how many times it's passed
 * to watch.
 *
 * @param  Atom<T>
 * @param  (value: T, previousValue: T, atomInstance: Atom<t>) => void
 * @return Atom<T>
 */
export function watch<T>(atomInstance: Atom<T>, callback: Watcher<T>): Atom<T> {
  atomInstance[WATCHERS] = atomInstance[WATCHERS].add(callback);
  return atomInstance;
}

/**
 * Unregisters a watch callback.
 *
 * @param  Atom<T>
 * @param  (value: T, previousValue: T, atomInstance: Atom<t>) => void
 * @return Atom<T>
 */
export function unwatch<T>(
  atomInstance: Atom<T>,
  callback: Watcher<T>
): Atom<T> {
  atomInstance[WATCHERS] = atomInstance[WATCHERS].remove(callback);
  return atomInstance;
}
